import React, { useCallback } from 'react';
import { bool, func, number, oneOfType, string } from 'prop-types';
import InputMask from 'react-input-mask';
import './styles.scss';

const Input = ({
  id,
  label,
  type = 'text',
  value,
  defaultValue = '',
  onChange,
  placeholder = '',
  disabled = false,
  maxLength,
  className = '',
  smallPrint = '',
  errorMsg = '',
  borderColor = '',
  imgSrc = '',
  mask = '',
  readOnly = false,
  autoComplete = 'off',
  onKeyDown,
  onKeyPress,
  onBlur,
  ...props
}) => {
  const hasMessage = Boolean(errorMsg.length || smallPrint.length);
  const statusClass = borderColor === 'warn' ? 'warning' : borderColor;
  const rootClassName = [
    'input',
    'optimized-input',
    hasMessage ? 'input-span' : '',
    statusClass,
    className,
  ].filter(Boolean).join(' ');

  const controlClassName = [
    'optimized-input-control',
    'input-group',
    borderColor ? `input-group-${borderColor}` : 'border',
    'rounded',
  ].join(' ');

  const handleChange = useCallback((event) => {
    if (disabled) return;

    if (onChange) {
      onChange(event);
    }
  }, [disabled, onChange]);

  const valueProps = value !== undefined ? { value } : { defaultValue };
  const sharedInputProps = {
    ...props,
    id,
    name: id,
    type,
    disabled,
    maxLength,
    placeholder,
    readOnly,
    autoComplete,
    onChange: handleChange,
    onKeyDown,
    onKeyPress,
    onBlur,
    className: 'optimized-input-field form-control',
    ...valueProps,
  };

  return (
    <div className={rootClassName}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className={controlClassName}>
        {mask ? (
          <InputMask
            {...sharedInputProps}
            mask={mask}
            maskChar=" "
          />
        ) : (
          <input {...sharedInputProps} />
        )}
        {imgSrc.length ? (
          <span className="input-group-append">
            <div className="input-group-text bg-transparent">
              <img src={imgSrc} alt={borderColor} />
            </div>
          </span>
        ) : null}
      </div>
      {errorMsg.length ? <span className={`input-span-${borderColor || 'error'}`}>{errorMsg}</span> : null}
      {smallPrint.length && !errorMsg.length ? <span>{smallPrint}</span> : null}
    </div>
  );
};

Input.propTypes = {
  id: string.isRequired,
  label: string,
  type: string,
  value: oneOfType([string, number]),
  defaultValue: oneOfType([string, number]),
  onChange: func,
  placeholder: string,
  disabled: bool,
  maxLength: oneOfType([string, number]),
  className: string,
  smallPrint: string,
  errorMsg: string,
  borderColor: string,
  imgSrc: string,
  mask: string,
  readOnly: bool,
  autoComplete: string,
  onKeyDown: func,
  onKeyPress: func,
  onBlur: func,
};

export default Input;
